import HeaderBrand from '@Guidelines/Shell/AppShell/AppHeader/Brand/HeaderBrand';
import FacilitySelector from '@Guidelines/Shell/AppShell/AppHeader/FacilitySelector/FacilitySelector';
import MenuToggleButton from '@Guidelines/Shell/AppShell/AppHeader/MenuToggle/MenuToggleButton';
import PrimaryNav from '@Guidelines/Shell/AppShell/AppHeader/PrimaryNav/PrimaryNav';
import SettingsToggleButton from '@Guidelines/Shell/AppShell/AppHeader/SettingsToggle/SettingsToggleButton';
import UserMenu from '@Guidelines/Shell/AppShell/Menu/UserMenu/UserMenu';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';

/**
 * `flex-shrink: 0` keeps the header at its natural height inside `AppShell`'s column and leaves the
 * remainder to the row below. It is not `sticky`: the document never scrolls (`html { overflow: hidden }`),
 * each screen scrolls inside `AppMain`, so the header never leaves the viewport to begin with.
 */
const headerStyle = css`
	flex-shrink: 0;
	background-color: ${theme.appBar.background};
	border-bottom: 1px solid ${theme.appBar.border};
`;

const brandStripeStyle = css`
	display: flex;
	width: 100%;
	height: 4px;
`;

const brandStripeBandStyle = (color: string) => css`
	flex: 1;
	background-color: ${color};
`;

const rowStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	height: 4.5rem;
	max-width: ${theme.layout.maxWidth};
	margin: 0 auto;
	padding: 0 ${theme.layout.gutter};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		gap: 0.75rem;
		height: 4rem;
		padding: 0 1rem;
	}
`;

const leftClusterStyle = css`
	display: flex;
	flex: 1;
	align-items: center;
	gap: 2rem;
	min-width: 0;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex: 0 1 auto;
		gap: 0.5rem;
	}
`;

const rightClusterStyle = css`
	display: flex;
	align-items: center;
	gap: 1rem;
	min-width: 0;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		gap: 0.75rem;
	}
`;

/**
 * Top bar whose row is centered and capped at `layout.maxWidth` — the brand's left edge sits on the content column's axis on every route — with the brand stripe full width below: menu toggle (narrow viewports), brand
 * and primary navigation (wide viewports) on the left; facility switcher, settings toggle (wide viewports)
 * and avatar on the right; closed by the brand stripe.
 */
const AppHeader: React.FC = () => {
	return (
		<header css={headerStyle}>
			<div css={rowStyle}>
				<div css={leftClusterStyle}>
					<MenuToggleButton />
					<HeaderBrand />
					<PrimaryNav />
				</div>
				<div css={rightClusterStyle}>
					<FacilitySelector />
					<SettingsToggleButton />
					<UserMenu />
				</div>
			</div>
			<div css={brandStripeStyle} aria-hidden="true">
				<div css={brandStripeBandStyle(theme.appBar.brandStripe.cyan)} />
				<div css={brandStripeBandStyle(theme.appBar.brandStripe.green)} />
				<div css={brandStripeBandStyle(theme.appBar.brandStripe.amber)} />
				<div css={brandStripeBandStyle(theme.appBar.brandStripe.raspberry)} />
			</div>
		</header>
	);
};

export default AppHeader;
